import api, { handleApiResponse, handleApiError } from './api';

class StaffExpertiseService {
  // Get staff member's expertise
  async getStaffExpertise(userId) {
    try {
      const response = await api.get(`/staff/${userId}/expertise`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Add expertise to staff member
  async addStaffExpertise(userId, data) {
  try {
    console.log('Service: Adding expertise with data:', data);
    const response = await api.post(`/staff/${userId}/expertise`, data);
    return handleApiResponse(response);
  } catch (error) {
    console.error('Service error details:', error.response?.data);
    
    // Check if it's a duplicate error
    if (error.response?.status === 422) {
      const errorData = error.response.data;
      if (errorData.errors?.category_id) {
        // Extract the specific error message
        const categoryError = Array.isArray(errorData.errors.category_id) 
          ? errorData.errors.category_id[0] 
          : errorData.errors.category_id;
        throw new Error(categoryError);
      }
    }
    
    throw new Error(handleApiError(error));
  }
}

  // Update staff expertise
  async updateStaffExpertise(userId, expertiseId, data) {
    try {
      const response = await api.patch(`/staff/${userId}/expertise/${expertiseId}`, data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Remove staff expertise
async removeStaffExpertise(userId, expertiseId) {
  try {
    const response = await api.delete(`/staff/${userId}/expertise/${expertiseId}`);
    return handleApiResponse(response); // Use handleApiResponse instead of response.data
  } catch (error) {
    //specific error handling for 404 cases
    if (error.response?.status === 404) {
      throw new Error(`Expertise with ID ${expertiseId} not found for user ${userId}`);
    }
    // For other errors, use the existing error handling
    throw new Error(handleApiError(error));
  }
}


  // Get available categories for expertise assignment
  async getAvailableCategories() {
    try {
      const response = await api.get('/staff/expertise/categories');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get experts for a specific category
  async getExpertsForCategory(categoryId) {
    try {
      const response = await api.get(`/categories/${categoryId}/experts`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Find best agent for category
  async findBestAgent(categoryId, priority = 'medium') {
    try {
      const response = await api.post(`/categories/${categoryId}/find-best-agent`, {
        priority
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Validate expertise hierarchy
  async validateExpertiseHierarchy(userId, categoryId) {
    try {
      const response = await api.post(`/staff/${userId}/expertise/validate-hierarchy`, {
        category_id: categoryId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Bulk update expertise
  async bulkUpdateExpertise(updates) {
    try {
      const response = await api.post('/staff/expertise/bulk-update', {
        updates
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get available experts for ticket
  async getAvailableExperts(ticketId) {
    try {
      const response = await api.get(`/tickets/${ticketId}/available-experts`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Assign ticket by expertise
  async assignByExpertise(ticketId, data) {
    try {
      const response = await api.post(`/tickets/${ticketId}/assign-by-expertise`, data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Helper methods
  formatCategoryHierarchy(categories) {
    return categories.map(category => ({
      ...category,
      label: `${'  '.repeat(category.level)}${category.name}`,
      value: category.id,
      depth: category.level
    }));
  }

  getStaffLevelDisplay(level) {
    const levels = {
      1: 'Level 1 (Junior)',
      2: 'Level 2 (Senior)', 
      3: 'Level 3 (Lead)'
    };
    return levels[level] || 'Unknown Level';
  }

  getHierarchyMatchTypeDisplay(type) {
    const types = {
      'exact_match': 'Exact Match',
      'parent_expertise': 'Parent Category Expert',
      'child_expertise': 'Specialized Expert',
      'no_match': 'No Match'
    };
    return types[type] || type;
  }

  getHierarchyMatchTypeColor(type) {
    const colors = {
      'exact_match': 'bg-green-100 text-green-800',
      'parent_expertise': 'bg-blue-100 text-blue-800', 
      'child_expertise': 'bg-purple-100 text-purple-800',
      'no_match': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors['no_match'];
  }



  validateExpertiseData(data) {
    const errors = {};
    
    if (!data.category_id) {
      errors.category_id = 'Category is required';
    }
    
    if (data.max_concurrent_tickets !== undefined) {
      if (!Number.isInteger(data.max_concurrent_tickets) || data.max_concurrent_tickets < 1 || data.max_concurrent_tickets > 50) {
        errors.max_concurrent_tickets = 'Max concurrent tickets must be between 1 and 50';
      }
    }
    
    if (data.notes && data.notes.length > 500) {
      errors.notes = 'Notes cannot exceed 500 characters';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Format expertise data for display
  formatExpertiseForDisplay(expertise) {
    return {
      ...expertise,
      category_display: expertise.category?.full_path || expertise.category?.name,
      staff_level_display: this.getStaffLevelDisplay(expertise.staff_level),
      workload_status: this.getWorkloadStatus(expertise),
      capacity_percentage: this.calculateCapacityPercentage(expertise)
    };
  }

  getWorkloadStatus(expertise) {
    if (!expertise.current_category_workload || !expertise.max_concurrent_tickets) {
      return 'unknown';
    }
    
    const percentage = (expertise.current_category_workload / expertise.max_concurrent_tickets) * 100;
    
    if (percentage === 0) return 'available';
    if (percentage <= 50) return 'light';
    if (percentage <= 80) return 'moderate';
    if (percentage < 100) return 'heavy';
    return 'full';
  }

  getWorkloadStatusColor(status) {
    const colors = {
      'available': 'bg-green-100 text-green-800',
      'light': 'bg-blue-100 text-blue-800',
      'moderate': 'bg-yellow-100 text-yellow-800',
      'heavy': 'bg-orange-100 text-orange-800',
      'full': 'bg-red-100 text-red-800',
      'unknown': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors['unknown'];
  }

  calculateCapacityPercentage(expertise) {
    if (!expertise.current_category_workload || !expertise.max_concurrent_tickets) {
      return 0;
    }
    return Math.min(100, Math.round((expertise.current_category_workload / expertise.max_concurrent_tickets) * 100));
  }

  // Sort experts by priority score
  sortExpertsByStaffLevel(experts) {
    return experts.sort((a, b) => {
      // Sort by staff level (descending) then by current workload (ascending)
      if (a.staff_level !== b.staff_level) {
        return (b.staff_level || 0) - (a.staff_level || 0);
      }
      return (a.current_workload || 0) - (b.current_workload || 0);
    });
  }

  // Filter available experts
  filterAvailableExperts(experts) {
    return experts.filter(expert => 
      expert.is_available && 
      expert.available_capacity > 0
    );
  }

  // Group experts by staff level
  groupExpertsByStaffLevel(experts) {
    return experts.reduce((groups, expert) => {
      const level = expert.staff_level || 0;
      const key = level > 0 ? `Level ${level}` : 'No Level';
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(expert);
      
      return groups;
    }, {});
  }
}

const staffExpertiseService = new StaffExpertiseService();
export default staffExpertiseService;